/**
 * Clerk auth styling — landing Sign In with Google + email/password.
 *
 * - clerkAppearance: Clerk appearance API (variables + elements)
 * - clerkAuthSupplementalStyles: CSS for pseudo-selectors, hover, layout fixes
 *   (injected after Clerk mounts — see applyClerkAuthSupplementalStyles)
 *
 * Logo is rendered by Landing.js from src/assets/logos (BRAND_LOGO) — Clerk logo is hidden.
 */
export const clerkAppearance = {
  layout: {
    // Logo is rendered by Landing.js from assets/logos — hide Clerk's own logo slot
    logoPlacement: "none",
    socialButtonsPlacement: "top",
    socialButtonsVariant: "blockButton",
  },
  variables: {
    colorPrimary: "#667eea",
    colorBackground: "#141414",
    colorInputBackground: "#222222",
    colorInputText: "#f5f5f5",
    colorText: "#f5f5f5",
    colorTextSecondary: "#c8c8c8",
    colorTextOnPrimaryBackground: "#ffffff",
    colorNeutral: "#2e2e2e",
    colorDanger: "#ef4444",
    colorSuccess: "#22c55e",
    colorWarning: "#f59e0b",
    borderRadius: "0.875rem",
    fontFamily: "Inter, sans-serif",
    fontSize: "1.125rem",
    spacingUnit: "1.2rem",
  },
  elements: {
    form: {
      gap: "1.5rem",
    },
    rootBox: {
      width: "100%",
      maxWidth: "100%",
    },
    cardBox: {
      width: "100%",
      maxWidth: "100%",
      boxShadow: "none",
    },
    card: {
      width: "100%",
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      padding: "0",
      gap: "1.25rem",
    },
    headerTitle: {
      display: "none",
    },
    headerSubtitle: {
      display: "none",
    },
    logoBox: {
      display: "none",
    },
    logoImage: {
      display: "none",
    },
    formFieldLabel: {
      fontSize: "1.15rem",
      fontWeight: 500,
      marginBottom: "0",
      color: "#e8e8e8",
    },
    formFieldInput: {
      fontSize: "1.125rem",
      minHeight: "46px",
      height: "46px",
      padding: "14px 16px",
      color: "#f5f5f5",
      backgroundColor: "#222222",
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid #444444",
    },
    formFieldHintText: {
      fontSize: "1.0625rem",
      color: "#b0b0b0",
    },
    formFieldInfoText: {
      fontSize: "1.0625rem",
      color: "#b0b0b0",
    },
    formFieldSuccessText: {
      fontSize: "1.0625rem",
    },
    formFieldWarningText: {
      fontSize: "1.0625rem",
    },
    formFieldErrorText: {
      fontSize: "1.0625rem",
    },
    formButtonPrimary: {
      fontSize: "1.6rem",
      minHeight: "46px",
      height: "46px",
      padding: "14px 18px",
      fontWeight: 500,
    },
    socialButtonsBlockButton: {
      fontSize: "1.125rem",
      minHeight: "46px",
      height: "46px",
      padding: "16px 18px",
      backgroundColor: "#f3f3f3",
      border: "1px solid #d8d8d8",
      color: "#1a1a1a",
      width: "100%",
      boxSizing: "border-box",
    },
    socialButtonsBlockButtonText: {
      fontSize: "1.5rem",
      fontWeight: 500,
      color: "#1a1a1a",
    },
    dividerLine: {
      backgroundColor: "#444444",
    },
    dividerText: {
      fontSize: "1rem",
      color: "#b0b0b0",
    },
    footerAction: {
      display: "none",
    },
    footerActionText: {
      fontSize: "1.0625rem",
    },
    footerActionLink: {
      fontSize: "1.0625rem",
    },
    identityPreviewText: {
      fontSize: "1.0625rem",
    },
    formResendCodeLink: {
      fontSize: "1.0625rem",
    },
    otpCodeFieldInputs: {
      display: "flex",
      justifyContent: "space-between",
      gap: "8px",
      width: "100%",
    },
    otpCodeFieldInput: {
      width: "42px",
      minWidth: "42px",
      maxWidth: "42px",
      height: "48px",
      minHeight: "48px",
      fontSize: "1.25rem",
      fontWeight: 600,
      textAlign: "center",
      color: "#f5f5f5",
      backgroundColor: "#222222",
      border: "1px solid #666666",
      borderRadius: "0.75rem",
      boxSizing: "border-box",
    },
    identityPreviewEditButton: {
      color: "#8b9cff",
    },
    devModeNotice: {
      fontSize: "0.875rem",
    },
  },
};

/** Override Clerk default copy — single Google continue CTA. */
export const clerkLocalization = {
  socialButtonsBlockButton: "Continue with {{provider|titleize}}",
  signIn: {
    start: {
      title: "",
      subtitle: "",
    },
  },
  signUp: {
    start: {
      title: "",
      subtitle: "",
    },
  },
};

/** CSS that Clerk appearance API cannot express (placeholders, hover, layout). */
export const clerkAuthSupplementalStyles = `
.clerk-auth-wrapper,
.clerk-auth-wrapper .cl-rootBox,
.clerk-auth-wrapper .cl-card,
.clerk-auth-wrapper .cl-formFieldLabel,
.clerk-auth-wrapper .cl-formButtonPrimary,
.clerk-auth-wrapper .cl-socialButtonsBlockButton,
.clerk-auth-wrapper input,
.clerk-auth-wrapper button {
  font-family: var(--font-primary);
}

.clerk-auth-wrapper .cl-rootBox,
.clerk-auth-wrapper .cl-cardBox,
.clerk-auth-wrapper .cl-card,
.clerk-auth-wrapper .cl-main,
.clerk-auth-wrapper .cl-scrollBox,
.clerk-auth-wrapper .cl-internal,
.clerk-auth-wrapper form {
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
  box-sizing: border-box !important;
}

.clerk-auth-wrapper form {
  gap: 1.5rem !important;
}

/* Clerk wraps fields + Continue in an internal flex column — same gap as DevTools tweak */
.clerk-auth-wrapper form > div {
  gap: 1.5rem !important;
}

.clerk-auth-wrapper .cl-formFieldRow,
.clerk-auth-wrapper .cl-formField,
.clerk-auth-wrapper .cl-formFieldInputGroup,
.clerk-auth-wrapper .cl-formFieldInput__input,
.clerk-auth-wrapper .cl-phoneInputBox {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.clerk-auth-wrapper .cl-otpCodeField,
.clerk-auth-wrapper .cl-otpCodeFieldInputs {
  width: 100% !important;
  max-width: 100% !important;
  display: flex !important;
  justify-content: space-between !important;
  gap: 8px !important;
  box-sizing: border-box !important;
}

.clerk-auth-wrapper .cl-otpCodeFieldInput,
.clerk-auth-wrapper input.cl-otpCodeFieldInput {
  width: 42px !important;
  min-width: 42px !important;
  max-width: 42px !important;
  height: 48px !important;
  min-height: 48px !important;
  padding: 0 !important;
  margin: 0 !important;
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  line-height: 48px !important;
  text-align: center !important;
  color: #f5f5f5 !important;
  background-color: #222222 !important;
  border: 1px solid #666666 !important;
  border-radius: 0.75rem !important;
  box-sizing: border-box !important;
  caret-color: #8b9cff !important;
}

.clerk-auth-wrapper .cl-otpCodeFieldInput:focus,
.clerk-auth-wrapper input.cl-otpCodeFieldInput:focus {
  outline: none !important;
  border-color: #8b9cff !important;
  box-shadow: 0 0 0 2px rgba(139, 156, 255, 0.25) !important;
}

.clerk-auth-wrapper .cl-otpCodeFieldInput::placeholder,
.clerk-auth-wrapper input.cl-otpCodeFieldInput::placeholder {
  color: #777777 !important;
  opacity: 1 !important;
  font-size: 1.25rem !important;
}

.clerk-auth-wrapper .cl-socialButtons,
.clerk-auth-wrapper .cl-socialButtonsBlockButton {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.clerk-auth-wrapper .cl-headerTitle,
.clerk-auth-wrapper .cl-headerSubtitle,
.clerk-auth-wrapper .cl-logoBox,
.clerk-auth-wrapper .cl-logoImage {
  display: none !important;
}

.clerk-auth-wrapper input.cl-formFieldInput:not(.cl-otpCodeFieldInput),
.clerk-auth-wrapper input.cl-input:not(.cl-otpCodeFieldInput),
.clerk-auth-wrapper .cl-formFieldInput:not(.cl-otpCodeFieldInput),
.clerk-auth-wrapper input[type="email"],
.clerk-auth-wrapper input[type="password"] {
  font-size: 1.125rem !important;
  min-height: 46px !important;
  height: 46px !important;
  padding: 14px 16px !important;
  color: #f5f5f5 !important;
  background-color: #222222 !important;
  border: 1px solid #444444 !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  border-radius: 0.875rem !important;
}

.clerk-auth-wrapper input.cl-formFieldInput:not(.cl-otpCodeFieldInput)::placeholder,
.clerk-auth-wrapper input.cl-input:not(.cl-otpCodeFieldInput)::placeholder,
.clerk-auth-wrapper input[type="email"]::placeholder,
.clerk-auth-wrapper input[type="password"]::placeholder {
  font-size: 1.125rem !important;
  color: #9a9a9a !important;
  opacity: 1 !important;
}

.clerk-auth-wrapper .cl-formFieldLabel,
.clerk-auth-wrapper .cl-formFieldLabelRow,
.clerk-auth-wrapper .cl-formFieldLabelRow label {
  font-size: 1.15rem !important;
  font-weight: 500 !important;
  color: #e8e8e8 !important;
  margin-bottom: 0 !important;
}

.clerk-auth-wrapper .cl-formFieldHintText,
.clerk-auth-wrapper .cl-formFieldInfoText,
.clerk-auth-wrapper .cl-formFieldSuccessText,
.clerk-auth-wrapper .cl-formFieldWarningText,
.clerk-auth-wrapper .cl-formFieldErrorText {
  font-size: 1.0625rem !important;
  line-height: 1.45 !important;
}

.clerk-auth-wrapper .cl-formButtonPrimary,
.clerk-auth-wrapper .cl-formButtonPrimary span {
  font-size: 1.1875rem !important;
  min-height: 46px !important;
  height: 46px !important;
  font-weight: 600 !important;
}

.clerk-auth-wrapper .cl-socialButtonsBlockButton {
  min-height: 46px !important;
  height: 46px !important;
  padding: 16px 18px !important;
  background-color: #f3f3f3 !important;
  border: 1px solid #d8d8d8 !important;
  color: #1a1a1a !important;
  gap: 12px !important;
}

.clerk-auth-wrapper .cl-socialButtonsBlockButton:hover {
  background-color: #ffffff !important;
  border-color: #cccccc !important;
}

.clerk-auth-wrapper .cl-socialButtonsBlockButtonText {
  font-size: 1.5rem !important;
  font-weight: 500 !important;
  color: #1a1a1a !important;
  line-height: 1.3 !important;
}

.clerk-auth-wrapper .cl-socialButtonsProviderIcon,
.clerk-auth-wrapper .cl-socialButtonsBlockButton img {
  width: 22px !important;
  height: 22px !important;
  flex-shrink: 0 !important;
}

.clerk-auth-wrapper .cl-dividerText {
  font-size: 1rem !important;
  color: #b0b0b0 !important;
}

.clerk-auth-wrapper .cl-footerAction {
  display: none !important;
}
`;

export const CLERK_AUTH_STYLE_ID = "clerk-auth-supplemental-styles";

export function applyClerkAuthSupplementalStyles() {
  let styleEl = document.getElementById(CLERK_AUTH_STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = CLERK_AUTH_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = clerkAuthSupplementalStyles;
}
